import React from 'react';
import { InputNumber, Space, theme } from 'antd';
import type { InputNumberProps } from 'antd';
import { parseInputNumber } from '../../utils/normalizeNumber';

const { useToken } = theme;

/**
 * NumericInputのプロパティ型
 * InputNumberPropsからaddonAfter/addonBeforeを除外し、独自に定義
 */
type NumericInputProps = Omit<InputNumberProps, 'addonAfter' | 'addonBefore'> & {
  /** 入力欄の後ろに表示するアドオン（単位など） */
  addonAfter?: React.ReactNode;
  /** 入力欄の前に表示するアドオン */
  addonBefore?: React.ReactNode;
};

/**
 * 全角数字を半角数字に自動変換するInputNumber
 *
 * @description
 * Ant DesignのInputNumberをラップし、全角数字（０-９）を
 * 半角数字（0-9）に自動変換する機能を標準で提供します。
 * addonAfter/addonBeforeはSpace.Compactでラップして表示します。
 *
 * @example
 * // 基本的な使い方
 * <NumericInput min={0} />
 *
 * // addonAfterを使用する場合
 * <NumericInput min={0} addonAfter="年" />
 *
 * // カスタムparserを使用する場合（全角変換は維持される）
 * <NumericInput
 *   parser={(value) => {
 *     // カスタム処理
 *     return parseFloat(value?.replace('$', '') || '0');
 *   }}
 * />
 */
export const NumericInput: React.FC<NumericInputProps> = ({
  parser,
  addonAfter,
  addonBefore,
  style,
  ...props
}) => {
  const { token } = useToken();

  /** アドオン用のスタイル（Ant Design 5のテーマトークンを使用） */
  const addonStyle: React.CSSProperties = {
    padding: '0 11px',
    color: token.colorText,
    fontWeight: 'normal',
    fontSize: '14px',
    textAlign: 'center',
    backgroundColor: token.colorFillAlter,
    border: `1px solid ${token.colorBorder}`,
    display: 'inline-flex',
    alignItems: 'center',
  };

  // カスタムparserがある場合は、全角変換後にカスタム処理を適用
  const combinedParser = (displayValue: string | undefined) => {
    // まず全角→半角変換を実行
    const halfWidthValue = parseInputNumber(displayValue);

    // カスタムparserがある場合は、変換後の値で実行
    if (parser) {
      return parser(String(halfWidthValue));
    }

    return halfWidthValue;
  };

  // addonAfter または addonBefore がある場合は Space.Compact でラップ
  if (addonAfter || addonBefore) {
    return (
      <Space.Compact style={style}>
        {addonBefore && <span style={addonStyle}>{addonBefore}</span>}
        <InputNumber {...props} parser={combinedParser} style={{ flex: 1 }} />
        {addonAfter && <span style={addonStyle}>{addonAfter}</span>}
      </Space.Compact>
    );
  }

  return <InputNumber {...props} parser={combinedParser} style={style} />;
};

export default NumericInput;
